<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in visibleRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" :color="textColor(route)" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAuthStore } from '../../stores/auth'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const auth = useAuthStore()

    const value = ref<boolean[]>([])

    // Show a menu item when the user's role is allowed, OR (§15) when the item opts into a
    // permission the user holds — so custom roles see the screens they can reach. A parent
    // with children is hidden when none of its children are visible.
    const roleOk = (item: INavigationRoute) =>
      (!!item.meta.permission && auth.can(item.meta.permission as string)) ||
      !item.meta.roles ||
      (auth.role !== null && item.meta.roles.includes(auth.role))

    // Filter children first, then keep a group if any child survives (so a role-gated
    // parent still appears when a custom role can see one of its children). Leaves use roleOk.
    const visibleRoutes = computed<INavigationRoute[]>(() =>
      navigationRoutes.routes
        .map((r) => (r.children ? { ...r, children: r.children.filter(roleOk) } : r))
        .filter((r) => (r.children ? r.children.length > 0 : roleOk(r))),
    )

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    /**
     * True when the current route IS this nav item, or is one of its detail pages
     * (e.g. `measures/:id` keeps "Resolutions & Ordinances" active).
     *
     * Matches on the route NAME, not a path suffix. `path.endsWith(name)` used to produce
     * false positives whenever one route name ended with another: `/measure-reports` ends
     * with `reports` (Tagged Reports), so opening Legislative Reports also expanded and
     * highlighted Tagging; `/citizen-tags` ends with `tags`; and every `*-dashboard` route
     * ended with `dashboard`.
     */
    const matchesNavItem = (name: string) =>
      route.name === name || route.path.startsWith(`/${name}/`)

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return matchesNavItem(section.name)
      }

      return section.children.some(({ name }) => matchesNavItem(name))
    }

    const setActiveExpand = () =>
      (value.value = visibleRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    // Dark sidebar (brand Ink) for contrast against the warm content area.
    const color = computed(() => '#1F2D24')
    const activeColor = computed(() => colorToRgba(getColor('primary'), 0.24))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#F26674' : '#9FB2A7')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#FFFFFF' : '#DCE5DF')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      visibleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
